import {
  type BulkUpdateAttributeDefinition,
  BulkUpdateConfirmationDefinition,
  getBulkUpdateRegistry,
  ValueSource,
  WriteStrategy,
} from '@provision/bulk-update-config';
import type { EntityName } from '@provision/entity-types';
import { arrayIfy } from '@provision/formatting-utils';
import { statesOptions } from 'src/components/AddSiteModal/use-new-site-form';
import { CustomViewEntity } from 'src/graphql/entities/custom-view/custom-view-config/custom-view-config';
import { SupportedActionQueries } from 'src/pages/Settings/rules/use-rules-engine';

const CUSTOM_VIEW_ENTITY_TO_ENTITY_NAME: Record<CustomViewEntity, EntityName> = {
  conversation: 'Conversation',
  site: 'Address',
  client: 'Client',
  project: 'Project',
};

export type BulkUpdateActionKind = 'update' | 'delete';

export interface BulkUpdateActionDefinition {
  id: string;
  label: string;
  icon: string;
  attributeKey: string;
  attributeLabel: string;
  updateAttribute: string;
  editorType: string;
  optionsQuery?: SupportedActionQueries;
  options?: { label: string; value: string; id: string }[];
  allowsClear?: boolean;
  submitMode: string;
  valueSource: ValueSource;
  /** Set only for `ValueSource.HostContext` actions: the value the hosting page supplied. */
  contextValue?: string;
  kind: BulkUpdateActionKind;
  accessRuleKey?: string[];
  confirmation?: BulkUpdateConfirmationDefinition;
}

type StaticOptionOverride = { label: string; value: string; id: string }[];

const STATIC_OPTIONS_OVERRIDES: Partial<Record<string, StaticOptionOverride>> = {
  'Address:state': statesOptions.map((s) => ({ id: s.value, label: s.label, value: s.value })),
};

function toActionDefinition(
  def: BulkUpdateAttributeDefinition,
  contextValues?: BulkActionContextValues,
): BulkUpdateActionDefinition {
  const label = def.actionLabel || def.label;

  const overrideKey = `${def.entityName}:${def.attribute}`;
  const staticOptions =
    STATIC_OPTIONS_OVERRIDES[overrideKey] ??
    (def.editor.staticOptions as StaticOptionOverride | undefined);

  const valueSource = def.editor.valueSource ?? ValueSource.Editor;

  // `kind` picks the submit path, not the styling. The 'delete' path renders no editor and goes
  // straight to confirm-and-submit, which suits both value-less soft deletes and actions whose
  // value comes from the hosting page. Anything that needs the user to choose a value stays
  // 'update' and signals destructiveness via the registry's `confirmation` instead.
  const kind: BulkUpdateActionKind =
    def.execution.writeStrategy === WriteStrategy.SoftDelete ||
    valueSource === ValueSource.HostContext
      ? 'delete'
      : 'update';

  const accessRuleKey = arrayIfy(def.validation.accessRuleKey);

  return {
    id: `${kind}-${def.attribute}`,
    label,
    icon: def.editor.icon ?? 'edit',
    attributeKey: def.attribute,
    attributeLabel: def.label,
    updateAttribute: def.attribute,
    editorType: def.editor.type,
    optionsQuery: def.editor.optionsSource as SupportedActionQueries | undefined,
    options: staticOptions,
    allowsClear: def.editor.allowsClear,
    submitMode: def.editor.submitMode ?? 'bulk-update',
    valueSource,
    contextValue:
      valueSource === ValueSource.HostContext ? contextValues?.[def.attribute] : undefined,
    kind,
    accessRuleKey,
    confirmation: def.confirmation,
  };
}

/**
 * Values supplied by the page hosting the grid, keyed by attribute. An action declaring
 * `ValueSource.HostContext` is offered only when its value is present here, which is what scopes
 * "Remove client from sites" to a client's Sites tab.
 */
export type BulkActionContextValues = Record<string, string | undefined>;

export function getBulkUpdateActions(
  entity: CustomViewEntity,
  {
    isMultitenancy = false,
    contextValues,
  }: { isMultitenancy?: boolean; contextValues?: BulkActionContextValues } = {},
): BulkUpdateActionDefinition[] {
  const entityName = CUSTOM_VIEW_ENTITY_TO_ENTITY_NAME[entity];
  return (
    getBulkUpdateRegistry()
      .filter((def) => def.entityName === entityName)
      .filter((def) => !isMultitenancy || def.isMultitenancyAllowed)
      .map((def) => toActionDefinition(def, contextValues))
      // A host-context action is only offered where the page actually supplied its value, which is
      // what scopes it to one surface. Filtering on the resolved value keeps this in step with the
      // value the submit path will send.
      .filter(
        (action) =>
          action.valueSource !== ValueSource.HostContext || Boolean(action.contextValue),
      )
  );
}

export function mapCustomViewEntityToEntityName(entity: CustomViewEntity): EntityName {
  return CUSTOM_VIEW_ENTITY_TO_ENTITY_NAME[entity];
}
